import { defineStore } from 'pinia';
import { ref } from 'vue';
import { WebPubSubClient } from '@azure/web-pubsub-client';
import { useNotificationStore } from './notifications-store';
import NotificationMessage from 'src/types/notification/notification-message';
import NotificationService from 'src/services/notification-service';
import { MessageRequest } from 'src/types/notification/message-request';
import { INotificationReceived, NotificationReceived } from 'src/types/notification/notification';
import jwtDecode from 'jwt-decode';

export const useWebSocketStore = defineStore('websocket', {
	state: () => ({
		clients: null as { userId: string, socket: WebPubSubClient | null, connected: boolean, connecting: boolean, retryCount: number }[] | null,
		maxRetries: 5,
	}),
	actions:{
		getClientAccessUrl(aid: string): Promise<string>{
			return new Promise<string>(async (resolve, reject) => {
				const notificationStore = useNotificationStore();
				if (!notificationStore.token) {
					await notificationStore.getToken(aid);
				}
				const decoded = jwtDecode(notificationStore.token!) as { exp: number };
				if(decoded.exp < Date.now() / 1000) {
					console.log('Token expired, refreshing...');
					await notificationStore.getToken(aid);
				}

				if (!notificationStore.token) {
					reject(new Error('Token is not available.'));
					return;
				}
		
				
				const response = await NotificationService.getClientAccessUrl(notificationStore.token);
				if (!response) {
					reject(new Error('Invalid response: URL is missing.'));
					return;
				}

				resolve(response);
				return;
			})
		},
		connect(aid: string){
			aid = aid.toString();
			return new Promise<boolean>(async (resolve, reject) => {
				if (this.clients && this.clients.find(client => client.userId == aid)?.connected) {
					console.log('Already connected.');
					resolve(true)
					return;
				}
		
				if (this.clients && this.clients.find(client => client.userId == aid)?.connecting) {
					console.log('Connection in progress; skip re-connecting...');
					resolve(true);
					return;
				}
		
				let client = this.clients?.find(client => client.userId == aid);

				if (!client) {
					client = { userId: aid, socket: null, connected: false, connecting: false, retryCount: 0 };
					this.clients ? this.clients.push(client) : this.clients = [client];
				}

				client.connecting = true;
		
				try {
					const accessUrl = await this.getClientAccessUrl(aid);
					const wpsClient = new WebPubSubClient(accessUrl);
					
					wpsClient.on('connected', () => {
						const client = this.clients?.find(client => client.userId == aid)!;
						client.connected = true;
						client.connecting = false;
						client.retryCount = 0;
						console.log('WebPubSubClient connected.');
					});
		
					wpsClient.on('disconnected', async e => {
						const client = this.clients?.find(client => client.userId == aid);
						if(!client) return;
						client.connected = false;
						client.connecting = false;
						console.warn('WebPubSubClient disconnected.', e);
		
						// if (client.retryCount < this.maxRetries) {
						// 	client.retryCount++;
						// 	const delay = computeDelay(client.retryCount);
						// 	console.log(`Retry attempt #${client.retryCount} after ${delay} ms`);
						// 	await sleep(delay);
						// 	await this.connect(aid);
						// } else {
						// 	console.error('Max reconnection attempts reached.');
						// }
					});
		
					wpsClient.on('server-message', event => {
						console.log('Server message received:', event);

						if( event.message.data.toString().includes("Handler not found")) return;

						const messageData = event.message.data as NotificationMessage;

						if( messageData.type === "response") return;

						const notification = new NotificationReceived({
							id: messageData.Id,
							userId: messageData.AffiliateUserId,
							scenario: messageData.Type,
							content: messageData.Content,
							isDelivered: true,
							deliveredAt: messageData.Created,
							readBy: [],
							created: messageData.Created,
						} as INotificationReceived);

						useNotificationStore().addNotification(notification, messageData.AffiliateUserId);
					});

					wpsClient.on("group-message", (event) => {
						console.log("Group message received:", event);
						const messageData = event.message.data;
					});
					
					await wpsClient.start();
					client.socket = wpsClient;
					resolve(true);
				} catch (error) {
					client.connecting = false;
					reject(error);
				}
			});
		},
		reconnect(){
			return new Promise<void>(async (resolve, reject) => {
				if (!this.clients) {
					resolve();
					return;
				}
		
				for (const client of this.clients) {
					if (client.socket) {
						try{
							client.socket.start().catch((error) => {
								console.log('WebPubSub already connected');
							})
						}
						catch (error) {	
							client.connected = false;
							client.connecting = false;
							this.connect(client.userId);
						}
					}
					else{
						client.connected = false;
						client.connecting = false;
						this.connect(client.userId);
					}
				}
				resolve();
			})
		},
		disconnect(){
			return new Promise<void>(async (resolve, reject) => {
				useNotificationStore().invalidateToken();
				if (this.clients && this.clients.length > 0) {
					for (const client of this.clients) {
						if (client.socket) {
							try{
								client.socket.stop();
							}
							catch (error) {
								console.error('Error stopping WebPubSubClient:', error);
							}
							client.socket = null;
							client.connected = false;
							client.connecting = false;
							client.retryCount = 0;
						}
					}
					this.clients = null;
					resolve();
				} else {
					resolve();
				}
			});
		},
		sendScenarioMessage(scenarioPayload: MessageRequest, aid: string) {
			aid = aid.toString();
			return new Promise<boolean>(async (resolve, reject) => {
				if (!this.clients || !this.clients.find(client => client.userId == aid)?.connected) {
					reject(new Error('Not connected'));
					return;
				}
				console.log('Sending scenario message:', scenarioPayload);
				try {
					await this.clients.find(c => c.userId === aid)!.socket!.sendEvent('message', scenarioPayload, 'json');
					resolve(true);
				} catch (error) {
					reject(error);
				}
			})
	}	,
	},
	persist: true
});